import React, { useRef, useState } from "react";

const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const XLS_MIME = "application/vnd.ms-excel";

export function isRfpExcelFile(file) {
  if (!file) return false;
  const name = file.name?.toLowerCase() || "";
  const type = file.type || "";
  if (type === XLSX_MIME || type === XLS_MIME) return true;
  return name.endsWith(".xlsx") || name.endsWith(".xls");
}

export function isRfpUploadFile(file) {
  if (!file) return false;
  const name = file.name?.toLowerCase() || "";
  const type = file.type || "";
  if (type === "application/pdf" || name.endsWith(".pdf")) return true;
  if (type === DOCX_MIME || name.endsWith(".docx")) return true;
  return isRfpExcelFile(file);
}

export default function RfpUploadZone({ file, onFile, disabled, busy, compact = false }) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const pick = (f) => {
    if (!f || disabled) return;
    if (!isRfpUploadFile(f)) return;
    onFile(f);
  };

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        pick(e.dataTransfer.files?.[0]);
      }}
      onClick={() => !disabled && inputRef.current?.click()}
      className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed px-3 text-center transition ${
        compact ? "min-h-[3rem] py-2" : "min-h-[4.5rem] py-3"
      } ${
        disabled
          ? "cursor-not-allowed border-slate-200 bg-slate-50 opacity-60 dark:border-slate-800 dark:bg-slate-900/50"
          : dragOver
            ? "border-emerald-500 bg-emerald-50/80 dark:border-emerald-500 dark:bg-emerald-950/30"
            : "border-slate-300 bg-slate-50/80 hover:border-emerald-400 hover:bg-emerald-50/50 dark:border-slate-600 dark:bg-slate-950/40 dark:hover:border-emerald-600"
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx,application/vnd.ms-excel,.xls"
        className="hidden"
        disabled={disabled}
        onChange={(e) => pick(e.target.files?.[0])}
      />
      {file ? (
        <>
          <p className="truncate max-w-full text-sm font-medium text-emerald-800 dark:text-emerald-200">{file.name}</p>
          {!compact && <p className="mt-0.5 text-xs text-slate-500">Drop or browse to replace</p>}
        </>
      ) : (
        <>
          <p className={`text-slate-700 dark:text-slate-200 ${compact ? "text-xs" : "text-sm"}`}>
            {busy ? "Uploading…" : compact ? "Drop PDF, Word, or Excel" : "Drop PDF, Word, or Excel here or browse"}
          </p>
          {!compact && <p className="mt-0.5 text-xs text-slate-500">PDF, .docx, .xlsx, or .xls</p>}
        </>
      )}
    </div>
  );
}
